/**
 * Shared data-validation engine (TypeScript side).
 * Behaviour-identical to backend/utils/validation_engine.py — locked by the
 * shared fixture validation/testcases.json (see engine.parity.test.ts).
 *
 * Validates FORMAT only. Required-ness is handled by useFieldValidation.
 */
import catalogJson from './_generated/catalog.json';

export interface ValidationResult { valid: boolean; message?: string; }
export interface CatalogEntry {
  code: number; kind: string; message: string;
  minLen?: number; maxLen?: number; trimEdges?: boolean; pattern?: string; fn?: string;
  allowNegative?: boolean; min?: number; max?: number; inclusive?: boolean;
  minYear?: number; requireSpecial?: boolean; extraChars?: string;
}

const CATALOG: Record<number, CatalogEntry> = {};
(catalogJson as CatalogEntry[]).forEach((e) => { CATALOG[e.code] = e; });

type Additional = Record<string, any> | null | undefined;

const digits = (s: string) => (s || '').replace(/\D/g, '');

const CTRL_RE = /[\x00-\x08\x0b\x0c\x0e-\x1f\x7f]/;
const XSS_RE = /<\s*\/|<\s*script|<\s*(iframe|img|svg|object|embed)|javascript:|on\w+\s*=|<\s*[a-zA-Z]/i;
const SQLI_RE = /union\s+select|;\s*drop\s+table|;\s*delete\s+from|'\s*or\s+1\s*=\s*1/i;
function securityUnsafe(s: string): boolean { return XSS_RE.test(s) || SQLI_RE.test(s); }

function parseDate(value: any): Date | null {
  if (value == null) return null;
  const s = String(value).split('T')[0];
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;
  return dt;
}

const LOCAL_LENGTHS: Record<string, number[]> = {
  '+267': [7, 8], '+1': [10], '+91': [10], '+44': [10],
};

const CUSTOM: Record<string, (value: any, add: Additional) => boolean> = {
  phoneIntl: (v) => /^\+?\d{8,15}$/.test(String(v ?? '')),
  phoneBotswana: (v) => /^\+267\d{7,8}$/.test(String(v ?? '')),
  phoneLenient: (v) => {
    const s = String(v ?? '').trim();
    if (s === '') return true;
    return /^\+?\d{8,15}$/.test(s.replace(/[ \-()]/g, ''));
  },
  phoneLocal: (v, add) => {
    const cc = add?.country_code;
    const d = digits(String(v ?? ''));
    const allowed = LOCAL_LENGTHS[cc];
    if (!allowed) return d.length >= 7 && d.length <= 15;
    return allowed.includes(d.length);
  },
  phoneE164: (v) => /^\+\d{8,15}$/.test(String(v ?? '')),
  regexCustom: (v, add) => {
    const p = add?.pattern;
    if (!p) return false;
    try { return new RegExp(`^(?:${p})$`).test(String(v ?? '')); } catch { return false; }
  },
  dateRange: (_v, add) => {
    const a = parseDate(add?.from_date); const b = parseDate(add?.to_date);
    return !!a && !!b && a.getTime() <= b.getTime();
  },
  numericRange: (_v, add) => {
    const lo = Number(add?.min_value); const hi = Number(add?.max_value);
    if (Number.isNaN(lo) || Number.isNaN(hi)) return false;
    return lo <= hi;
  },
  dateGapMinDays: (_v, add) => {
    const a = parseDate(add?.from_date); const b = parseDate(add?.to_date);
    const minDays = Number(add?.min_days);
    if (!a || !b || Number.isNaN(minDays)) return false;
    const days = Math.round((b.getTime() - a.getTime()) / 86400000);
    return days >= minDays;
  },
};

const DECLARATIVE: Record<string, (e: CatalogEntry, v: any) => boolean> = {
  text: (e, v) => {
    // Leading/trailing whitespace is trimmed silently (by the backend's
    // InputSecurityMiddleware before persistence) rather than rejected here
    // — trimEdges no longer blocks the field, it's just accepted as-typed.
    const s = v == null ? '' : String(v);
    if (securityUnsafe(s) || CTRL_RE.test(s)) return false;
    const t = s.trim();
    if (e.maxLen != null && t.length > e.maxLen) return false;
    return t.length >= (e.minLen ?? 0);
  },
  regex: (e, v) => new RegExp(e.pattern as string).test(v == null ? '' : String(v)),
  integer: (e, v) => {
    const s = (v == null ? '' : String(v)).trim();
    const re = e.allowNegative ? /^-?\d+$/ : /^\d+$/;
    if (!re.test(s)) return false;
    const n = parseInt(s, 10);
    if (e.min != null && n < e.min) return false;
    if (e.max != null && n > e.max) return false;
    return true;
  },
  number: (e, v) => {
    const s = (v == null ? '' : String(v)).trim();
    if (!/^-?\d+(\.\d+)?$/.test(s)) return false;
    const n = parseFloat(s);
    const inclusive = e.inclusive ?? true;
    if (e.min != null && (n < e.min || (!inclusive && n === e.min))) return false;
    if (e.max != null && (n > e.max || (!inclusive && n === e.max))) return false;
    return true;
  },
  date: (e, v) => {
    const d = parseDate(v);
    if (!d) return false;
    if (e.minYear != null && d.getFullYear() < e.minYear) return false;
    return true;
  },
  datetime: (_e, v) => {
    const s = String(v ?? '');
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(s)) return false;
    return !!parseDate(s.split('T')[0]);
  },
  password: (e, v) => {
    const s = v == null ? '' : String(v);
    if (s.length < (e.minLen ?? 0)) return false;
    if (e.requireSpecial && !/[^A-Za-z0-9]/.test(s)) return false;
    return true;
  },
  identity: (e, v) => {
    // Leading/trailing whitespace is trimmed silently (by the backend's
    // InputSecurityMiddleware before persistence) rather than rejected here
    // — validate the trimmed value so the field never blocks on edge spaces.
    const raw = v == null ? '' : String(v);
    const s = raw.trim();
    if (CTRL_RE.test(s) || securityUnsafe(s)) return false;
    const n = s.length;
    if (n < (e.minLen ?? 0) || n > (e.maxLen ?? Infinity)) return false;
    const extra = e.extraChars ?? '';
    for (const ch of s) {                 // iterates code points
      if (/\p{L}/u.test(ch)) continue;    // Unicode letter, any script
      if (ch >= '0' && ch <= '9') continue;
      if (extra.includes(ch)) continue;
      return false;
    }
    return true;
  },
  description: (e, v) => {
    const s = v == null ? '' : String(v);
    if (s === '') return true;
    if (s.includes('<') || s.includes('>')) return false;
    if (CTRL_RE.test(s) || securityUnsafe(s)) return false;
    return s.length <= (e.maxLen ?? Infinity);
  },
};

export function validate(code: number, value: any, additional?: Additional): ValidationResult {
  const entry = CATALOG[code];
  if (!entry) return { valid: false, message: `Unknown validation code ${code}` };
  const ok = entry.kind === 'custom'
    ? CUSTOM[entry.fn as string](value, additional)
    : DECLARATIVE[entry.kind](entry, value);
  return ok ? { valid: true } : { valid: false, message: entry.message };
}

export function maxLenForCode(code: number): number | undefined {
  return CATALOG[code]?.maxLen;
}

export interface FieldSpec {
  name: string; code: number; value: any;
  additional?: Additional; required?: boolean;
}

/** Validate many fields → map of name → error message (failures only). */
export function validateFields(specs: FieldSpec[]): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const s of specs) {
    const isEmpty = s.value == null || (typeof s.value === 'string' && s.value.trim() === '');
    if (isEmpty && !s.required) continue;
    if (isEmpty && s.required) { errors[s.name] = 'This field is required'; continue; }
    const r = validate(s.code, s.value, s.additional);
    if (!r.valid) errors[s.name] = r.message as string;
  }
  return errors;
}
